import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { WorkflowFinding } from "../../../services/treatmentPlan.service";

/**
 * Why a generated workflow cannot be activated yet — frame `3i`.
 *
 * 🔴 The rule the whole panel turns on: **"Warnings don't block activation.
 * Errors do."** The designer's note gives the reasoning — an unresolved agent
 * is an environment problem that blocks, while a short pull window is a
 * clinical judgement the designer is allowed to make. So severity is not
 * decoration here; it decides whether the primary action is available.
 *
 * Findings are grouped by the STEP they belong to, because a reviewer's first
 * question is "which card is wrong?". The workflow panel itself stays
 * read-only — the design forbids per-step state on the cards — so the step is
 * named in text here instead of badged over there.
 *
 * Remedies send a MESSAGE rather than opening an editor. That is not a
 * shortcut: the hand-off is explicit that "every change to a generated plan
 * goes through the conversation, which keeps one source of truth". A button
 * that edited the workflow directly would create a second way to change it.
 */

type Props = {
	findings: WorkflowFinding[];
	/** Called with a prompt the reviewer can send to the generator. */
	onAskToFix?: (finding: WorkflowFinding, prompt: string) => void;
	onActivate?: () => void;
	isActivating?: boolean;
};

/** `days[12].d1-bup1` -> `Day 12 · d1-bup1`. */
export const describeStep = (path?: string): string => {
	if (!path) return "This workflow";

	const match = /^days\[([^\]]+)\]\.(.+)$/.exec(path);
	if (!match) return path;

	const [, day, actionId] = match;

	return actionId === "?" ? `Day ${day}` : `Day ${day} · ${actionId}`;
};

/**
 * A short label for the kind of problem, as `3i` uses ("Unresolved agent",
 * "Missing incentive", "Check").
 *
 * Derived from the finding's code so a new check gets a readable label without
 * this file being edited — the fallback turns `earnings-daily-ceiling` into
 * "Earnings daily ceiling" rather than rendering a slug.
 */
export const describeCode = (finding: WorkflowFinding): string => {
	const known: Record<string, string> = {
		"agent-unresolved": "Unresolved agent",
		"agent-not-offerable": "Agent unavailable",
		"agent-not-selected": "No agent selected",
		"agent-reference-malformed": "Incomplete agent reference",
		"earnings-annual-cap": "Over the annual cap",
		"earnings-no-ceiling-configured": "No ceiling configured",
		"not-assessed-branch-missing": "Missing outcome",
		"reward-outcome-unauthored": "Missing outcome",
		"coverage-not-decided": "Coverage not decided",
		"coverage-threshold-missing": "Missing threshold",
		"tir-range-wrong-unit": "Wrong unit",
		"tir-range-inverted": "Impossible range",
		"tir-range-absent": "No target range",
		"integration-provider-unimplemented": "Unsupported integration",
		"integration-data-source-unproduced": "Data not produced",
	};

	if (known[finding.code]) return known[finding.code];
	if (finding.severity === "WARN") return "Check";

	const words = finding.code.replace(/-/g, " ");

	return words.charAt(0).toUpperCase() + words.slice(1);
};

/** What to say to the generator to get this fixed. */
const fixPrompt = (finding: WorkflowFinding): string =>
	[
		`${describeStep(finding.path)}: ${finding.message}`,
		finding.suggestion ?? "",
		"Please update the workflow accordingly.",
	]
		.filter(Boolean)
		.join(" ");

const severityStyles = (severity: WorkflowFinding["severity"]) =>
	severity === "ERROR"
		? { border: "error.light", chip: "error" as const }
		: { border: "warning.light", chip: "warning" as const };

const WorkflowFindingsPanel: React.FC<Props> = ({
	findings,
	onAskToFix,
	onActivate,
	isActivating,
}) => {
	const actionable = findings.filter((finding) => finding.severity !== "INFO");
	if (actionable.length === 0) return null;

	const errors = actionable.filter((finding) => finding.severity === "ERROR");
	const blocked = errors.length > 0;

	// Errors first: the reviewer cannot activate until those are gone, so a
	// warning above an error would bury the thing standing in their way.
	const ordered = [
		...errors,
		...actionable.filter((finding) => finding.severity === "WARN"),
	];

	return (
		<Paper
			variant="outlined"
			sx={{ p: 2.5, borderColor: blocked ? "error.main" : "warning.main" }}
			data-testid="workflow-findings"
		>
			<Typography variant="h6" sx={{ mb: 0.5 }}>
				{blocked
					? "This workflow can't be activated yet"
					: "Worth checking before you activate"}
			</Typography>

			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				{blocked
					? `${errors.length} ${errors.length === 1 ? "step needs" : "steps need"} attention before this plan can go live.`
					: "Nothing here blocks activation."}
			</Typography>

			<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
				{ordered.map((finding, index) => {
					const styles = severityStyles(finding.severity);

					return (
						<Box
							key={`${finding.code}-${finding.path ?? index}`}
							sx={{
								border: 1,
								borderColor: styles.border,
								borderRadius: 1,
								p: 1.5,
							}}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									mb: 0.5,
									flexWrap: "wrap",
								}}
							>
								<Typography variant="subtitle2">
									{describeStep(finding.path)}
								</Typography>
								<Chip
									size="small"
									color={styles.chip}
									variant="outlined"
									label={describeCode(finding)}
								/>
							</Box>

							<Typography
								variant="body2"
								sx={{ mb: finding.suggestion ? 0.5 : 0 }}
							>
								{finding.message}
							</Typography>

							{finding.suggestion && (
								<Typography variant="body2" color="text.secondary">
									{finding.suggestion}
								</Typography>
							)}

							{onAskToFix && (
								<Button
									size="small"
									sx={{ mt: 1 }}
									onClick={() => onAskToFix(finding, fixPrompt(finding))}
								>
									Ask the generator to fix this
								</Button>
							)}
						</Box>
					);
				})}
			</Box>

			<Divider sx={{ my: 2 }} />

			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 2,
					flexWrap: "wrap",
				}}
			>
				<Typography variant="caption" color="text.secondary">
					Warnings don&apos;t block activation. Errors do.
				</Typography>

				{onActivate && (
					<Button
						variant="contained"
						onClick={onActivate}
						disabled={blocked || isActivating}
					>
						Activate treatment plan
					</Button>
				)}
			</Box>
		</Paper>
	);
};

export default WorkflowFindingsPanel;
